import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  AbstractControl,
  ValidatorFn,
  FormArray,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { UserService } from 'src/app/service/user.service';
import {
  ValidateEmailRequest,
  UserStatus,
  ValidatedUser,
} from '../../../shared/models/ValidatedUser';

import { SupportDialogComponent } from 'src/app/core/components/support-dialog/support-dialog.component';
import { ToastPopUpComponents } from 'src/app/shared/services/toast-popup.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastOptions } from 'src/app/core/components/toast/toast.component';
import { firstValueFrom } from 'rxjs';
import { TranslationService } from 'src/app/service/api/translation.service';
import { TranslationRequest } from 'src/app/shared/models/TranslationRequest';
import { SentenceCasePipe } from 'src/app/shared/pipes/sentence-case.pipe';
import { LoginService } from 'src/app/service/api/login.service';
import { RecaptchaService } from 'src/app/service/recaptcha.service';

@Component({
  selector: 'app-forgot-username',
  providers:[SentenceCasePipe],
  template: ` <div id="forgotUserName" class="forgotUserNameContent">
      <div>
        <h1 role="heading" id="forgotUserHeading" [attr.aria-label]="pageTitle| translation">
          {{ pageTitle | translation : translationRequest | sentenceCase}}
        </h1>
      </div>
      <div class="forgotUserName-container" [hidden]="!showSubmitUsername">
        <form
          novalidate
          (ngSubmit)="onforgotUserNameSubmit()"
          [formGroup]="forgotUserNameForm"
          role="none"
        >
          <ul class="" role="none">
            <li
              role="none"
              class="tw-w-full tw-flex tw-flex-col md:tw-flex-row app-mb2 tw-items-center app-mt3 tw-justify-end"
            >
              <label for="emailId" class="tw-w-full md:tw-w-[50%]"
                >{{ emailAddressQuestionText | translation : translationRequest | sentenceCase}}: *</label
              >
              <mat-form-field class="tw-w-full md:tw-w-[50%]">
                <input
                  matInput
                  autocomplete="off"
                  required
                  id="emailId"
                  email
                  type="email"
                  formControlName="email"
                  [attr.aria-label]="emailAddressQuestionText | translation : translationRequest | sentenceCase"
                  [ngClass]="{
                    'is-invalid':
                      forgotUserNameForm.get('email').dirty &&
                      !forgotUserNameForm.get('email').valid
                  }"
                />
                <mat-error
                  class="error-message"
                  role="none"
                  *ngIf="(forgotUserNameForm.get('email').errors?.['required'] || forgotUserNameForm.get('email').dirty) && !forgotUserNameForm.get('email').valid"
                >
                  <span>{{'Error : Please enter your email address.' | translation : translationRequest | sentenceCase}}</span>
                </mat-error>
              </mat-form-field>
            </li>
            <li
              role="none"
              class="tw-w-full tw-flex tw-flex-col md:tw-flex-row app-mt3 tw-justify-end app-mb3"
            >
              <div class="tw-w-full tw-flex tw-justify-center">
                <div #recaptchaContainer></div>
              </div>
            </li>
            <li
              role="none"
              class="tw-w-full tw-flex tw-justify-center app-mb3"
              *ngIf="captchaError"
            >
              <span class="error-message">{{ captchaError | sentenceCase }}</span>
            </li>
          </ul>

          <div dir="ltr" class="tw-w-full tw-flex tw-justify-end app-mt2">
            <app-button
              type="button"
              mat-raised-button
              color="primary"
              (click)="onCancel()"
              [attr.aria-label]="'Cancel' | translation : translationRequest | sentenceCase"
              emphasis="Low"
              class="app-pr2"
            >
              {{'Cancel' | translation : translationRequest | sentenceCase}}
            </app-button>
            <app-button
              mat-raised-button
              color="primary"
              type="submit"
              [title]="
                forgotUserNameForm.valid
                  ? ''
                  : ('Disabled until the form data is valid' | translation : translationRequest | sentenceCase)
              "
              [disabled]="!forgotUserNameForm.valid"
              [attr.aria-label]="'Next' | translation : translationRequest | sentenceCase"
              aria-hidden="true"
              emphasis="High"
            >
              {{'Next' | translation : translationRequest | sentenceCase }}
            </app-button>
          </div>
        </form>
      </div>
    </div>
    <br aria-hidden="true" />
    <div
      class="app-pl5 app-pr5 tw-flex tw-w-full md:tw-w-[50%]  "
      *ngIf="submittedRequest"
      (click)="showSupportModal ? openSupportDialog() : ''"
    >
      <app-toast
        [message]="confirmationMessage"
        [type]="toastEnumOptions"
        isToastFocus="true"
      ></app-toast>
    </div>`,
  styles: [
    `
      .forgotUserNameContent {
        margin: 40px;
      }
      .forgotUserName-container {
        width: 80%;
        max-width: 630px;
        margin: 30px;
      }

      .forgotUserName-container * {
        box-sizing: border-box;
      }

      .mb-20 {
        margin-bottom: 20px;
      }
      .mt-20 {
        margin-top: 20px;
      }
      .mat-select-underline {
        border-bottom: 1px solid var(--dark-blue);
      }

      .is-invalid {
        outline: 2;
        border-width: 0 0 2px;
        border-color: var(--red);
      }
    `,
  ],
})
export class ForgotUsernameComponent implements OnInit {
  @ViewChild('recaptchaContainer', { static: true })
  recaptchaContainer: ElementRef;
  pageTitle: string;
  forgotUserNameForm: UntypedFormGroup;
  emailAddressQuestionText: string;
  submittedRequest: boolean = false;
  showSubmitUsername: boolean = true;
  showSupportModal: boolean = false;
  confirmationMessage: string;
  forgotUsernameRequest: ValidateEmailRequest;
  toastEnumOptions: any;
  captchaError: string;
  recaptchaToken: string = '';
  recaptchaWidgetId: number = null;
  recaptchaInitialized = false;
  recaptchaSiteKey: string = '';
  toastPopupOptions = ToastPopUpComponents;
  animationsDisabled = true;
  translationRequest: TranslationRequest = new TranslationRequest(
    {
      language: sessionStorage.getItem('languagepreference'), // Default to English, you can make this dynamic
      key: null,
      contNum: 0,
      tableName: null,
      primaryKey: null,
      primaryValue: null,
      lob: null,
      group: 'LoginApp',
      subGroup: 'LoginApp'
    }
  );

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private titleService: Title,
    private userService: UserService,
    public dialog: MatDialog,
    public translationService: TranslationService,
    private sentenceCasePipe: SentenceCasePipe,
    private loginService: LoginService,
    private recaptchaService: RecaptchaService
  ) {}

  loadTranslations(): void {
      this.translationService.getTranslation(this.translationRequest);
    }

  ngOnInit(): void {
    this.loadTranslations();
    this.submittedRequest = false;
    this.showSubmitUsername = true;
    this.showSupportModal = false;
    this.captchaError = '';
    this.titleService.setTitle('mySedgwick - Forgot user name');
    this.pageTitle = this.sentenceCasePipe.transform('Forgot user name');
    this.emailAddressQuestionText =
      this.sentenceCasePipe.transform('Please provide the email address associated with your account');

    this.forgotUserNameForm = this.fb.group({
      email: ['', [Validators.required]],
    });

    void this.initializeRecaptcha();
  }
  onCancel(): void {
    this.router.navigate(['/']);
  }

  openSupportDialog(): void {
    const dialogRef = this.dialog.open(SupportDialogComponent);
  }

  async onforgotUserNameSubmit() {
    if (this.forgotUserNameForm.invalid) return;
    this.captchaError = '';
    const tokenFromWidget = this.recaptchaService.getResponse(
      this.recaptchaWidgetId
    );
    if (tokenFromWidget?.trim()) {
      this.recaptchaToken = tokenFromWidget;
    }
    if (!this.recaptchaToken?.trim()) {
      this.handleInvalidCaptcha();
      return;
    }

    try {
      this.forgotUsernameRequest = new ValidateEmailRequest({
        recaptchaToken: this.recaptchaToken,
        useremail: this.forgotUserNameForm.controls['email'].value,
        sendEmail: 'Y',
        LanguageCode: sessionStorage.getItem('languagepreference'),
      });

      const result = await firstValueFrom(
        this.userService.validateEmail(this.forgotUsernameRequest)
      );

      if (!result) {
        this.handleInvalidCaptcha();
        return;
      }
      
      switch (result.userStatus) {
        case UserStatus.BadCaptCha:
          this.handleInvalidCaptcha();
          break;
        case UserStatus.Valid:
        case UserStatus.NotFound:
        case UserStatus.Locked:
        case UserStatus.Disabled:
        case UserStatus.DeadClient:
        case UserStatus.ClientNotSetupYet:
          this.handleSuccessfulValidation();
          break;
      }
    } catch (error) {
      this.handleInvalidCaptcha();
    }
  }

  private handleInvalidCaptcha(): void {
    this.resetRecaptcha();
    this.captchaError = 'Please complete the reCAPTCHA verification before continuing.';
  }

  private async initializeRecaptcha(): Promise<void> {
    if (this.recaptchaInitialized) {
      return;
    }

    try {
      const { siteKey } = await firstValueFrom(
        this.loginService.getRecaptchaSiteKey()
      );
      this.recaptchaSiteKey = siteKey;
      await this.recaptchaService.ensureLoaded();
      if (
        this.recaptchaContainer?.nativeElement &&
        this.recaptchaWidgetId === null
      ) {
        const widgetId = this.recaptchaService.render(
          this.recaptchaContainer.nativeElement,
          this.recaptchaSiteKey,
          'FORGOT_USERNAME',
          {
            onSuccess: (token) => {
              this.recaptchaToken = token;
              this.captchaError = '';
            },
            onExpired: () => {
              this.recaptchaToken = '';
            },
            onError: () => {
              this.recaptchaToken = '';
            },
          }
        );
        if (widgetId !== null) {
          this.recaptchaWidgetId = widgetId;
          this.recaptchaInitialized = true;
        }
      }
    } catch (error) {
      console.error('Unable to load reCAPTCHA script.', error);
    }
  }

  private resetRecaptcha(): void {
    this.recaptchaToken = '';
    this.recaptchaService.reset(this.recaptchaWidgetId);
  }

  private handleSuccessfulValidation(): void {
    this.submittedRequest = true;
    this.showSubmitUsername = false;
    this.showSupportModal = true;
    
    // Split the message into translatable parts and HTML structure
    this.translationService.getTranslationForKey$('Thank you.', this.translationRequest).subscribe((thankYou) => {
      this.translationService.getTranslationForKey$('If a user name is found, a reset link will be sent to you by email.', this.translationRequest).subscribe((resetLinkMessage) => {
        this.translationService.getTranslationForKey$('Please check your Inbox, Junk and Spam folders for an email prior to contacting', this.translationRequest).subscribe((checkEmailMessage) => {
          this.translationService.getTranslationForKey$('Sedgwick Support', this.translationRequest).subscribe((supportText) => {
            this.translationService.getTranslationForKey$('technical support popup', this.translationRequest).subscribe((supportAriaLabel) => {
              this.translationService.getTranslationForKey$('You may close this browser window.', this.translationRequest).subscribe((closeWindowMessage) => {
                this.confirmationMessage = `${thankYou}</br>${resetLinkMessage}</br>${checkEmailMessage} <span role='link' tabindex='0' aria-label='${supportAriaLabel}' data-function=${this.toastPopupOptions.openSupportDialog} class='spanLink'>${supportText}</span>.</br>${closeWindowMessage}`;
              });
            });
          });
        });
      });
    });
    
    this.toastEnumOptions = ToastOptions.Info;
  }
}
